import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUp, ArrowDown } from 'lucide-react';

import EditPaychequeDrawer from '../../components/EditPaychequeDrawer';
import CompareModal from './components/CompareModal';
import EmployeeRow, { ROW_COLS } from './components/EmployeeRow';
import SubmitFooter from './components/SubmitFooter';
import { usePayRun } from './usePayRun';
import { openPreviewWindow } from './previewHtml';
import {
  authHeaders, fmtMoney, fmtDate, fmtDateFull,
  scheduleFrequencyLabel, mapStubToLine, computeTotals, computeTrend,
} from './helpers';
import { API_URL, FONT } from './constants';
import { C, tabular } from './styles';
import type { Line, Stub } from './types';

const EditPaychequeDrawerAny = EditPaychequeDrawer as any;

export default function PayrollPreview() {
  const { payRunId } = useParams<{ payRunId: string }>();
  const navigate = useNavigate();

  const {
    run, lines, payrollSettings, priorRuns, comparison, schedule,
    loading, error, isFinalized, setLines,
  } = usePayRun(payRunId);

  const [finalizing, setFinalizing] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | number | null>(null);
  const [compareFor, setCompareFor] = useState<Line | null>(null);

  useEffect(() => {
    if (isFinalized && payRunId) {
      navigate('/payroll/run/' + payRunId + '/done', { replace: true });
    }
  }, [isFinalized, payRunId, navigate]);

  const totals = useMemo(() => computeTotals(lines), [lines]);
  const trend = useMemo(() => computeTrend(priorRuns, totals.total_cost), [priorRuns, totals.total_cost]);

  const fundingConnected = Boolean(payrollSettings && payrollSettings.funding_account_id);
  const anyDirectDeposit = lines.some((l) => String(l.payment_method || '').toLowerCase().indexOf('direct') >= 0);
  const submitBlocked = anyDirectDeposit && !fundingConnected;
  const showFundingWarning = !fundingConnected;

  const handlePreview = (): void => {
    openPreviewWindow({ run, lines, payrollSettings });
  };

  const handleSaveForLater = async (): Promise<void> => {
    setSavingDraft(true);
    try {
      navigate('/payroll/overview');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (finalizing || submitBlocked || lines.length === 0) return;
    setFinalizing(true);
    try {
      const resp = await fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/finalize', {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const errText = await resp.text();
        alert('Failed to submit payroll: ' + errText);
        setFinalizing(false);
        return;
      }
      const finalizedRun = await resp.json();
      navigate('/payroll/run/' + payRunId + '/done', {
        state: { runData: finalizedRun, stubs: lines },
      });
    } catch {
      alert('Network error while submitting payroll. Please try again.');
      setFinalizing(false);
    }
  };

  const reloadStubs = async (): Promise<void> => {
    try {
      const r = await fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/stubs', {
        headers: authHeaders(),
      });
      if (!r.ok) return;
      const stubs: Stub[] = await r.json();
      if (!Array.isArray(stubs)) return;
      setLines(stubs.map(mapStubToLine));
    } catch { /* noop */ }
  };

  const postingAccount = (payrollSettings && payrollSettings.company_bank_name) || 'Not set';
  const scheduleFreq = schedule ? scheduleFrequencyLabel(schedule.frequency) : '';
  const scheduleName = schedule ? (schedule.name || scheduleFreq) : '';

  if (loading) {
    return (
      <div style={{ padding: '40px 32px', background: C.page, minHeight: '100vh', fontFamily: FONT }}>
        <div style={{ fontSize: 15, color: C.ink, fontWeight: 500 }}>Loading pay run...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px 32px', background: C.page, minHeight: '100vh', fontFamily: FONT }}>
        <div style={{ fontSize: 18, color: C.ink, fontWeight: 700, marginBottom: 8 }}>Could not load this pay run.</div>
        <div style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginBottom: 20 }}>{error}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 16px', background: C.card,
            border: '1.5px solid ' + C.ink, borderRadius: 10,
            color: C.ink, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: FONT,
          }}
        >
          Back to Payroll
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', margin: 0, padding: '28px 32px 110px', fontFamily: FONT, background: C.page, minHeight: '100vh' }}>
      <div style={{ background: C.card, borderRadius: 12, padding: '40px 48px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: C.ink, marginBottom: 14 }}>
          <a onClick={() => navigate('/payroll/overview')} style={{ color: C.ink, fontWeight: 600, textDecoration: 'none', opacity: 0.7, cursor: 'pointer' }}>Payroll</a>
          <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
          <a onClick={() => navigate('/payroll/run/' + payRunId)} style={{ color: C.ink, fontWeight: 600, textDecoration: 'none', opacity: 0.7, cursor: 'pointer' }}>Run payroll</a>
          <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
          <span style={{ color: C.ink, fontWeight: 700 }}>Review and submit</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40 }}>
          <div>
            <div style={{ fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: 6 }}>
              Review and submit
            </div>
            <div style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>Confirm the details before finalizing this pay run.</div>
          </div>
          {!submitBlocked && lines.length > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: C.brandBg, borderRadius: 6, fontSize: 12, color: C.brandDark, fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, background: C.brand, borderRadius: '50%' }} />
              READY TO SUBMIT
            </div>
          )}
        </div>

        {showFundingWarning && (
          <div style={{
            padding: '14px 18px', background: C.amberBg, borderRadius: 10,
            marginBottom: 40, display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              background: C.amberInk, color: C.card,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: 1, fontSize: 12, fontWeight: 700,
            }}>!</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Funding account not connected</div>
              <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 500 }}>All employees will be paid by cheque until you connect a bank account.</div>
            </div>
            <button
              onClick={() => navigate('/settings/payroll')}
              style={{
                padding: '8px 14px', background: C.card,
                border: '1.5px solid ' + C.ink, borderRadius: 8,
                color: C.ink, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Connect account
            </button>
          </div>
        )}

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32,
          paddingBottom: 32, borderBottom: '1px solid ' + C.line, marginBottom: 40,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>PAY SCHEDULE</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{scheduleName || 'Not set'}</div>
            {scheduleFreq && scheduleName !== scheduleFreq && (
              <div style={{ fontSize: 12, color: C.ink, fontWeight: 500, marginTop: 2 }}>{scheduleFreq}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>PAY PERIOD</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, ...tabular }}>
              {fmtDate(run?.pay_period_start)} to {fmtDate(run?.pay_period_end)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>PAY DATE</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, ...tabular }}>{fmtDateFull(run?.pay_date)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>POSTING ACCOUNT</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{postingAccount}</div>
          </div>
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>TOTAL PAYROLL COST</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
            <div style={{ fontSize: 56, fontWeight: 700, color: C.ink, letterSpacing: '-0.03em', lineHeight: 1, ...tabular }}>
              {fmtMoney(totals.total_cost)}
            </div>
            {trend && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', background: C.page, borderRadius: 999,
                fontSize: 12.5, color: C.ink, fontWeight: 700,
              }}>
                {trend.dir === 'up' ? <ArrowUp size={13} strokeWidth={2.5} /> : <ArrowDown size={13} strokeWidth={2.5} />}
                {trend.dir === 'up' ? 'Up' : 'Down'} {trend.abs}% vs last run
              </div>
            )}
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 40,
            paddingTop: 28, borderTop: '1px solid ' + C.line, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, color: C.ink, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: 8 }}>TO EMPLOYEES</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1, ...tabular, marginBottom: 6 }}>{fmtMoney(totals.net)}</div>
              <div style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>
                Net pay after deductions{totals.take_home_pct > 0 && ' · ' + totals.take_home_pct + '%'}
              </div>
            </div>
            <div style={{ width: 1, height: 90, background: C.line }} />
            <div>
              <div style={{ fontSize: 11, color: C.ink, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: 8 }}>TO CRA</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1, ...tabular, marginBottom: 6 }}>{fmtMoney(totals.cra_remittance)}</div>
              <div style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>Employee {fmtMoney(totals.employee_taxes)} + Employer {fmtMoney(totals.employer_taxes)}</div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.ink, marginBottom: 20, paddingBottom: 12, borderBottom: '1.5px solid ' + C.ink }}>
            Employees ({lines.length})
          </div>

          {lines.length === 0 ? (
            <div style={{ padding: '24px 0', fontSize: 14, color: C.ink, fontWeight: 500 }}>No employees included in this pay run.</div>
          ) : (
            <>
              <div style={{
                padding: '12px 0',
                display: 'grid', gridTemplateColumns: ROW_COLS, gap: 12,
                fontSize: 10, fontWeight: 700, color: C.ink,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderBottom: '1px solid ' + C.line,
              }}>
                <div>EMPLOYEE</div>
                <div style={{ textAlign: 'right' }}>TOTAL HOURS</div>
                <div style={{ textAlign: 'right' }}>GROSS PAY</div>
                <div style={{ textAlign: 'right' }}>EMPLOYEE TAXES & DEDUCTIONS</div>
                <div style={{ textAlign: 'right' }}>NET PAY</div>
                <div style={{ textAlign: 'right' }}>EMPLOYER TAXES & CONTRIBUTIONS</div>
                <div style={{ textAlign: 'right' }}>CHANGE IN GROSS PAY</div>
                <div style={{ textAlign: 'right' }}>METHOD</div>
              </div>

              {lines.map((l) => (
                <EmployeeRow
                  key={String(l.employee_id)}
                  line={l}
                  comparison={comparison}
                  onEdit={setEditingEmployeeId}
                  onCompare={setCompareFor}
                />
              ))}

              <div style={{
                padding: '16px 0',
                display: 'grid', gridTemplateColumns: ROW_COLS, gap: 12,
                alignItems: 'center',
                fontSize: 13.5, color: C.ink, fontWeight: 700, ...tabular,
                borderTop: '1.5px solid ' + C.ink,
              }}>
                <div>Total</div>
                <div style={{ textAlign: 'right' }}>{totals.total_hours}h</div>
                <div style={{ textAlign: 'right' }}>{fmtMoney(totals.gross)}</div>
                <div style={{ textAlign: 'right' }}>{fmtMoney(totals.employee_taxes)}</div>
                <div style={{ textAlign: 'right', fontSize: 15 }}>{fmtMoney(totals.net)}</div>
                <div style={{ textAlign: 'right' }}>{fmtMoney(totals.employer_taxes)}</div>
                <div />
                <div />
              </div>
            </>
          )}
        </div>

        <div style={{ marginBottom: 40, paddingBottom: 32, borderBottom: '1px solid ' + C.line }}>
          <div style={{ fontSize: 11, color: C.ink, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
            WHAT HAPPENS WHEN YOU SUBMIT
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {['Pay stubs generated for each employee', 'YTD balances updated and locked', 'Next pay run auto-created on schedule'].map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: C.ink, color: C.card,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: C.ink, fontWeight: 600, lineHeight: 1.5 }}>{text}</div>
              </div>
            ))}
          </div>
        </div>

        {submitBlocked && (
          <div style={{
            marginBottom: 20, padding: '12px 16px',
            background: '#FDECED', border: '1px solid #B4232A',
            borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#B4232A',
          }}>
            Submit is disabled: direct deposit is selected for some employees but no funding account is connected.
          </div>
        )}

        <SubmitFooter
          linesCount={lines.length}
          finalizing={finalizing}
          savingDraft={savingDraft}
          submitBlocked={submitBlocked}
          onBack={() => navigate('/payroll/run/' + payRunId)}
          onPreview={handlePreview}
          onSubmit={handleSubmit}
          onSaveForLater={handleSaveForLater}
        />

        {compareFor && (
          <CompareModal
            employee={compareFor}
            comparison={comparison}
            onClose={() => setCompareFor(null)}
          />
        )}

        {editingEmployeeId && (
          <EditPaychequeDrawerAny
            runId={payRunId}
            employeeId={editingEmployeeId}
            onClose={() => setEditingEmployeeId(null)}
            onSaved={reloadStubs}
          />
        )}

      </div>
    </div>
  );
}